import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, Loader2Icon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListSkeleton } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/form';
import { PhotoCapture } from '@/features/visit/photo-capture';
import { StepCheckin } from '@/features/visit/step-checkin';
import { StepPayment } from '@/features/visit/step-payment';
import { StepPtp } from '@/features/visit/step-ptp';
import { StepResult } from '@/features/visit/step-result';
import { StepSummary } from '@/features/visit/step-summary';
import { validateStep } from '@/features/visit/validate';
import { useAuth } from '@/lib/auth/context';
import { cn } from '@/lib/utils';
import type { WatermarkInfo } from '@/offline/media-queue';
import { loanSummary, visitedLoanIdsToday } from '@/offline/queries';
import { useOfflineQuery } from '@/offline/use-offline-query';
import { emptyDraft, loadDraft, saveDraft, type VisitDraft } from '@/offline/visit-draft';
import { submitVisit } from '@/offline/visit-submit';
import { APP_VERSION } from '@/sw/register';

export const Route = createFileRoute('/_app/kunjungan/$loanId')({
  component: VisitWizardPage,
});

const STEP_TITLES = ['Lokasi', 'Foto', 'Hasil', 'Rincian', 'Konfirmasi'];

/**
 * Wizard kunjungan (T-P7-17..23).
 *
 * Lima langkah dengan satu draf yang disimpan setiap perubahan. Langkah 4
 * berubah isi menurut hasil — pembayaran, janji bayar, atau dilewati — sehingga
 * jumlah langkah yang terlihat tetap sama dan collector tidak kehilangan orientasi
 * di tengah pengisian.
 */
function VisitWizardPage() {
  const { loanId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [draft, setDraft] = useState<VisitDraft | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: summary } = useOfflineQuery(() => loanSummary(loanId), [loanId]);
  const { data: visitedToday } = useOfflineQuery(
    async () => (await visitedLoanIdsToday()).has(loanId),
    [loanId],
  );

  useEffect(() => {
    void loadDraft(loanId).then((stored) => setDraft(stored ?? emptyDraft(loanId)));
  }, [loanId]);

  // Autosave: tiap perubahan langsung tersimpan, bukan hanya saat pindah langkah.
  // Aplikasi bisa tertutup di antara dua ketukan, dan foto yang hilang berarti
  // kunjungan diulang seluruhnya (NFR-UX-06).
  const patch = useCallback(
    (changes: Partial<VisitDraft>) =>
      setDraft((current) => {
        if (!current) return current;
        const next = { ...current, ...changes };
        void saveDraft(next);
        return next;
      }),
    [],
  );

  const watermark = useMemo<WatermarkInfo>(
    () => ({
      capturedAt: new Date(),
      latitude: draft?.checkin?.lat ?? null,
      longitude: draft?.checkin?.lng ?? null,
      collectorName: user?.fullName ?? '',
      loanNumber: summary?.loan.loanNumber ?? '',
    }),
    [draft?.checkin?.lat, draft?.checkin?.lng, user?.fullName, summary?.loan.loanNumber],
  );

  if (!draft || !summary) return <ListSkeleton rows={4} />;

  const arrears =
    Number.parseFloat(summary.loan.arrearsPrincipal || '0') +
    Number.parseFloat(summary.loan.arrearsInterest || '0') +
    Number.parseFloat(summary.loan.penalty || '0');

  const step = draft.step;
  const blocker = validateStep(draft, step);

  async function onSubmit() {
    if (!user || !draft || !summary) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await submitVisit(draft, {
        collectorId: user.id,
        collectorName: user.fullName,
        customerId: summary.loan.customerId,
        loanNumber: summary.loan.loanNumber,
        customerName: summary.customerName,
        appVersion: APP_VERSION,
      });

      await navigate({
        to: '/kunjungan',
        search: { kuitansi: result.receipt?.paymentClientUuid },
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Kunjungan gagal disimpan.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-[calc(100dvh-8rem)] flex-col">
      <div className="space-y-3 border-b border-border px-4 py-3">
        <div>
          <p className="truncate text-sm font-medium">{summary.customerName}</p>
          <p className="truncate text-xs text-muted-foreground">
            {summary.loan.loanNumber} · {summary.addressText ?? 'Alamat belum tercatat'}
          </p>
        </div>

        <ol className="flex gap-1" aria-label={`Langkah ${step} dari 5`}>
          {STEP_TITLES.map((title, index) => (
            <li key={title} className="flex-1">
              <span
                className={cn(
                  'block h-1 rounded-full',
                  index + 1 <= step ? 'bg-brand' : 'bg-muted',
                )}
              />
              <span
                className={cn(
                  'mt-1 block text-center text-[10px]',
                  index + 1 === step ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {title}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <div className="flex-1 space-y-4 px-4 py-4">
        {visitedToday && step === 1 ? (
          <Alert variant="warning">
            <span className="space-y-2">
              <span className="block">
                Pinjaman ini sudah dikunjungi hari ini. Kunjungan kedua ditolak kecuali Anda
                menegaskan bahwa itu memang disengaja.
              </span>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-[var(--color-brand)]"
                  checked={draft.overrideDailyLimit}
                  onChange={(event) => patch({ overrideDailyLimit: event.target.checked })}
                />
                Ya, kunjungan kedua ini disengaja.
              </label>
            </span>
          </Alert>
        ) : null}

        {step === 1 ? (
          <StepCheckin
            customerPosition={
              summary.address?.latitude != null && summary.address.longitude != null
                ? { lat: summary.address.latitude, lng: summary.address.longitude }
                : null
            }
            checkin={draft.checkin}
            onCheckin={(checkin) => patch({ checkin })}
          />
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            <header className="space-y-1">
              <h2 className="text-base font-semibold">Foto kunjungan</h2>
              <p className="text-sm text-muted-foreground">
                Minimal satu foto. Tanggal, koordinat, dan nama Anda dicetak otomatis pada foto.
              </p>
            </header>

            <PhotoCapture
              photos={draft.photos}
              onChange={(photos) => patch({ photos })}
              watermark={watermark}
            />
          </div>
        ) : null}

        {step === 3 ? <StepResult draft={draft} onChange={patch} /> : null}

        {step === 4 ? (
          draft.result === 'BAYAR' ? (
            <StepPayment
              draft={draft}
              totalObligation={arrears}
              watermark={watermark}
              onChange={patch}
            />
          ) : draft.result === 'JANJI_BAYAR' ? (
            <StepPtp draft={draft} arrears={arrears} onChange={patch} />
          ) : (
            <Alert>Hasil ini tidak memerlukan rincian tambahan. Lanjut ke konfirmasi.</Alert>
          )
        ) : null}

        {step === 5 ? (
          <StepSummary
            draft={draft}
            customerName={summary.customerName}
            loanNumber={summary.loan.loanNumber}
            onChange={patch}
          />
        ) : null}

        {error ? <Alert variant="danger">{error}</Alert> : null}
        {blocker ? <p className="text-xs text-muted-foreground">{blocker}</p> : null}
      </div>

      <div className="sticky bottom-0 flex gap-2 border-t border-border bg-surface px-4 py-3">
        <Button
          variant="secondary"
          fullWidth
          leading={<ArrowLeftIcon className="size-4" aria-hidden="true" />}
          onClick={() =>
            step === 1
              ? void navigate({ to: '/nasabah/$loanId', params: { loanId } })
              : patch({ step: step - 1 })
          }
        >
          {step === 1 ? 'Batal' : 'Kembali'}
        </Button>

        {step < 5 ? (
          <Button
            fullWidth
            disabled={blocker !== null}
            onClick={() => patch({ step: step + 1 })}
            leading={<ArrowRightIcon className="size-4" aria-hidden="true" />}
          >
            Lanjut
          </Button>
        ) : (
          <Button
            fullWidth
            disabled={blocker !== null || submitting}
            onClick={() => void onSubmit()}
            leading={
              submitting ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <CheckIcon className="size-4" aria-hidden="true" />
              )
            }
          >
            {submitting ? 'Menyimpan…' : 'Simpan kunjungan'}
          </Button>
        )}
      </div>
    </div>
  );
}
